'use client'

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AUTH_API_PATHS } from '@/lib/auth/paths'
import {
  IMPERSONATION_HINT_COOKIE,
  IMPERSONATION_HINT_COOKIE_VALUE,
} from '@/lib/auth/impersonationCookies'
import { notifyCookieChange, useCookieValue } from '@/Cookies/useCookies'
import { useStore } from '@/store'
import { fetchWithCloudflarePreclearance } from '@/lib/cloudflarePreclearance'

export const ADMIN_IMPERSONATION_STATUS_QUERY_KEY = ['AdminImpersonationStatus']

type ImpersonationStatusResponse = {
  success?: boolean
  transient?: boolean
  active?: boolean
  impersonation?: {
    sessionId: string
    customerId: number
    customerEmail?: string | null
    startedAt: string
  } | null
}

async function fetchImpersonationStatus(): Promise<ImpersonationStatusResponse> {
  const response = await fetchWithCloudflarePreclearance(
    AUTH_API_PATHS.ADMIN_IMPERSONATION_STATUS,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  )
  notifyCookieChange()

  if (!response.ok) {
    throw new Error(`Impersonation status failed: ${response.status}`)
  }

  const result = (await response
    .json()
    .catch(() => null)) as ImpersonationStatusResponse | null
  if (result?.success !== true) {
    throw new Error(
      result?.transient
        ? 'Impersonation status is temporarily unavailable'
        : 'Impersonation status returned an invalid response'
    )
  }

  return result
}

export function useImpersonationStatus() {
  const user = useStore(state => state.user)
  const authResolved = useStore(state => state.authResolved)
  const impersonationHintCookie = useCookieValue(IMPERSONATION_HINT_COOKIE)
  const hasImpersonationHint =
    impersonationHintCookie === IMPERSONATION_HINT_COOKIE_VALUE

  const query = useQuery({
    queryKey: ADMIN_IMPERSONATION_STATUS_QUERY_KEY,
    queryFn: fetchImpersonationStatus,
    enabled: hasImpersonationHint,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: 1,
  })

  const activeImpersonation = query.data?.active
    ? (query.data.impersonation ?? null)
    : null
  const isImpersonatingCurrentUser = useMemo(() => {
    if (!authResolved || !user || !activeImpersonation) {
      return false
    }

    return user.id === activeImpersonation.customerId
  }, [activeImpersonation, authResolved, user])

  return {
    ...query,
    activeImpersonation,
    isImpersonatingCurrentUser,
  }
}
